import { Injectable } from '@angular/core'
import { AgreementService } from '../agreement.service'
import { Observable } from 'rxjs'
import { IHistoryList } from '../../../models/interface/history-list.interface'
import { AgreementType } from '../../../models/enum/agreement.enum'
import { ISharedPagination } from '../../../models/interface/shared.interface'
import { catchError, map, retry } from 'rxjs/operators'
import { IAgreement, IAgreementEntry } from '../../../models/interface/agreement.interface'
import { SharedValue } from '../../../models/enum/shared.enum'
import { HttpClient, HttpParams } from '@angular/common/http'
import { AppConfigurationService } from '../../app-configuration.service'
import { FileService } from '../../file.service'
import { FileSaverService } from 'ngx-filesaver'
import { SharedService } from '../../shared/shared.service'

@Injectable({
  providedIn: 'root'
})
export class PriceSupportAgreementService {
  private type = AgreementType.PRICE_SUPPORT_AGREEMENT

  constructor(
    private agreementService: AgreementService,
    private http: HttpClient,
    private appConfigurationService: AppConfigurationService,
    private fileService: FileService,
    private fileSaverService: FileSaverService,
    private sharedService: SharedService
  ) {}

  public getAgreements(params?: ISharedPagination): Observable<IHistoryList> {
    return this.agreementService.getAgreements(AgreementType.PRICE_SUPPORT_AGREEMENT, params)
  }

  public getAllAgreements(size: number): Observable<IAgreement[]> {
    return this.getAgreements({ pageSize: size }).pipe(
      map((res) => {
        if (!res.results) {
          throw new Error(SharedValue.errorTryAgain)
        }
        return res.results
      })
    )
  }

  public getAgreementDetail(code: string): Observable<IAgreement> {
    return this.agreementService.getAgreementDetail(code)
  }

  public getAgreementDetailEntries(code: string, params?: ISharedPagination): Observable<IHistoryList> {
    return this.agreementService.getAgreementDetailEntries(code, AgreementType.PRICE_SUPPORT_AGREEMENT, params)
  }

  public getAgreementDetailAllEntries(code: string): Observable<IAgreementEntry[]> {
    return this.getAgreementDetail(code).pipe(
      map((res) => {
        if (!res.entries) {
          throw new Error(SharedValue.errorTryAgain)
        }
        return res.entries
      })
    )
  }

  public acceptAgreement(code: string, email: string): Observable<any> {
    const payload: HttpParams = new HttpParams().set('customerId', email)

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreement/${code}/accept`
    return this.http.post<any>(url, null, { params: payload }).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public rejectAgreement(code: string, email: string, reason: string): Observable<any> {
    const payload: HttpParams = new HttpParams().set('customerId', email).set('reason', reason)

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreement/${code}/reject`
    return this.http.post<any>(url, null, { params: payload }).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public getTermsAndConditionsPDF(agreement: IAgreement): void {
    const fileType = this.fileService.mimeConvertor('application/pdf')
    const fileName = agreement?.orderCode ? agreement?.orderCode + fileType : 'download' + fileType
    const blob = this.fileService.b64toBlobConvertor(agreement.externalQuoteDocumentBlob, 'application/pdf')
    this.fileSaverService.save(blob, fileName)
  }

  public updateAccountNumber(code: string, accountNumber: string): Observable<any> {
    const payload: HttpParams = new HttpParams().set('accountNumber', accountNumber)

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreement/${code}/updateAccountNumber`
    return this.http
      .post<any>(url, null, { params: payload })
      .pipe(retry(1), catchError(this.sharedService.handleError))
  }

  public downloadCSV(size: number): Observable<any> {
    const payload: HttpParams = new HttpParams().set('agreementType', this.type).set('pageSize', size.toString())

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreements/csv`
    return this.http
      .get(url, {
        params: payload,
        responseType: 'text'
      })
      .pipe(retry(1), catchError(this.sharedService.handleError))
  }
}
